/**
 * Two-level emotion atlas for reflections: primary category → specific emotion.
 * Meanings are looked up here (not invented by the model).
 * Keep the prompt list in getPersonalizedInsights.js in sync via buildEmotionAtlasPromptSection().
 */

export const OTHER_EMOTION_KEY = "Other";
export const CRISIS_EMOTION_KEY = "Needs Support";

/** Ordered primary categories (Emotion Atlas). */
export const EMOTION_CATEGORIES = [
  "Happy",
  "Strong",
  "Calm",
  "Surprised",
  "Sad",
  "Angry",
  "Fearful",
  "Disgusted"
];

/**
 * Atlas: category → specific emotions with CBT / decision-journal meanings.
 * Labels match stored `sentiment` values (PascalCase noun/adjective as shown).
 */
export const EMOTION_ATLAS = {
  Happy: [
    { key: "Joyful", meaning: "Something feels meaningful, secure, or worth keeping." },
    { key: "Hopeful", meaning: "You can picture a workable path forward." },
    { key: "Proud", meaning: "Your effort or values produced an outcome that matters to you." },
    { key: "Loved", meaning: "You interpret connection, care, or regard from someone important." },
    { key: "Peaceful", meaning: "Nothing in this moment requires defense or urgent action." },
    { key: "Curious", meaning: "Something new or uncertain invites examination." },
    { key: "Energetic", meaning: "You have capacity and readiness to act or create." },
    { key: "Aroused", meaning: "Something has raised your attention and activation." },
    { key: "Valued", meaning: "You read that your presence, effort, or view matters here." },
    { key: "Optimistic", meaning: "You expect usable options or favorable outcomes may appear." },
    { key: "Playful", meaning: "You feel free enough to explore or enjoy without heavy stakes." },
    { key: "Content", meaning: "What you have or where you are is enough for this moment." },
    { key: "Accepted", meaning: "You feel welcomed without needing to prove or hide parts of yourself." },
    { key: "Interested", meaning: "Something has captured your attention and seems worth understanding." }
  ],
  Strong: [
    { key: "Strong", meaning: "You sense steadiness, capacity, or resilience in this moment." },
    { key: "Empowered", meaning: "You recognize you can influence what happens next." },
    { key: "Faithful", meaning: "You stay committed to a person, purpose, or value that matters." },
    { key: "Appreciated", meaning: "Someone has noticed and valued your contribution or presence." },
    { key: "Courageous", meaning: "You are willing to act despite fear, discomfort, or uncertainty." },
    { key: "Confident", meaning: "You trust your judgment or readiness for the situation." },
    { key: "Successful", meaning: "An effort, goal, or milestone reached a meaningful outcome." },
    { key: "Creative", meaning: "Ideas or new options are available to work with." },
    { key: "Respected", meaning: "Your boundaries, contribution, or dignity are recognized." },
    { key: "Loyal", meaning: "You stay steadfast toward a relationship, group, or commitment." },
    { key: "Focused", meaning: "Your attention is gathered around a clear priority." }
  ],
  Calm: [
    { key: "Calm", meaning: "Your attention and tension have room to settle." },
    { key: "Connected", meaning: "You feel linked to someone, yourself, or this moment in a useful way." },
    { key: "Trusting", meaning: "You can rely on a person, process, or your own judgment here." },
    { key: "Thankful", meaning: "You notice something meaningful you have received or experienced." },
    { key: "Secure", meaning: "Needs for safety, stability, or belonging feel supported." },
    { key: "Thoughtful", meaning: "You are pausing to weigh meaning, impact, or care." },
    { key: "Relieved", meaning: "A burden, uncertainty, or threat has eased." },
    { key: "Belonging", meaning: "You feel welcomed and linked within a group or relationship." },
    { key: "Sensitive", meaning: "You are especially aware of emotional, relational, or sensory cues." },
    { key: "Loving", meaning: "You feel care, tenderness, or regard toward someone or something." },
    { key: "Serene", meaning: "You feel settled even while some things remain unresolved." },
    { key: "Relaxed", meaning: "High-alert tension has dropped; there is more room to think." },
    { key: "Safe", meaning: "You can stay present without needing to protect yourself right now." }
  ],
  Surprised: [
    { key: "Surprised", meaning: "Reality differed from what you predicted." },
    { key: "Confused", meaning: "The information or situation does not fit together yet." },
    { key: "Amazed", meaning: "You encountered something beyond what you expected." },
    { key: "Excited", meaning: "Something you want feels close or possible." }
  ],
  Sad: [
    { key: "Embarrassed", meaning: "You feel exposed after a social mistake or unwanted attention." },
    { key: "Indifferent", meaning: "You feel little pull, concern, or investment right now." },
    { key: "Depressed", meaning: "Your appraisal stays low, heavy, or disconnected from workable hope." },
    { key: "Remorseful", meaning: "You see that an action conflicted with your values or harmed someone." },
    { key: "Guilty", meaning: "You believe you did something wrong or failed a responsibility." },
    { key: "Fragile", meaning: "Your emotional resources feel thin and easily strained." },
    { key: "Isolated", meaning: "You feel cut off from meaningful connection or support." },
    { key: "Miserable", meaning: "Distress feels intense, prolonged, or hard to exit." },
    { key: "Bored", meaning: "Your attention needs stimulation, meaning, or a change of task." },
    { key: "Ashamed", meaning: "You are judging yourself as fundamentally flawed or unacceptable." },
    { key: "Sad", meaning: "Something valued has been lost, changed, or missed." },
    { key: "Lonely", meaning: "You need meaningful connection, not simply more people nearby." },
    { key: "Disappointed", meaning: "What happened did not match what you hoped for." },
    { key: "Hurt", meaning: "A relationship, word, or event touched something vulnerable." },
    { key: "Vulnerable", meaning: "Something important is exposed and the outcome is uncertain." }
  ],
  Angry: [
    { key: "Annoyed", meaning: "Something small but persistent is blocking you." },
    { key: "Stressed", meaning: "Demand feels high and your resources feel stretched." },
    { key: "Rage", meaning: "Anger is intense and may be hard to contain." },
    { key: "Hostile", meaning: "You feel strongly opposed, guarded, or ready to push back." },
    { key: "Selfish", meaning: "Your needs feel urgent, or you are focused mainly on protecting your own interest." },
    { key: "Furious", meaning: "Intense anger about a boundary, value, or fairness violation." },
    { key: "Irritated", meaning: "Something is wearing on your patience or comfort." },
    { key: "Skeptical", meaning: "You doubt whether a claim, promise, or intention is trustworthy." },
    { key: "Distrustful", meaning: "You are unable or unwilling to rely on someone or something." },
    { key: "Angry", meaning: "A boundary, value, need, or sense of fairness may have been violated." },
    { key: "Frustrated", meaning: "Something is blocking progress or repeating despite effort." },
    { key: "Critical", meaning: "Your mind detects a flaw, risk, or unmet standard." },
    { key: "Jealous", meaning: "Something you value feels threatened or more visible in someone else’s life." }
  ],
  Fearful: [
    { key: "Fearful", meaning: "You detect possible danger or high uncertainty." },
    { key: "Anxious", meaning: "Your mind is preparing for a future threat it cannot fully predict." },
    { key: "Overwhelmed", meaning: "Demand feels greater than your current capacity." },
    { key: "Insecure", meaning: "You are unsure of your belonging, ability, or value in this moment." }
  ],
  Disgusted: [
    { key: "Disgusted", meaning: "Something feels harmful, contaminating, offensive, or deeply misaligned." },
    { key: "Disapproving", meaning: "A behavior or choice conflicts with your standards or values." }
  ]
};

const CATEGORY_COLOR = {
  Happy: "#0d9488",
  Strong: "#059669",
  Calm: "#115e59",
  Surprised: "#ea580c",
  Sad: "#3b82f6",
  Angry: "#dc2626",
  Fearful: "#7c3aed",
  Disgusted: "#78716c"
};

/** Flat ordered list of specific emotions + crisis + Other (charts / allowlists). */
export const REFLECTION_EMOTION_ORDER = [
  ...EMOTION_CATEGORIES.flatMap((cat) => EMOTION_ATLAS[cat].map((e) => e.key)),
  CRISIS_EMOTION_KEY,
  OTHER_EMOTION_KEY
];

export const REFLECTION_EMOTION_LABELS = Object.fromEntries(
  REFLECTION_EMOTION_ORDER.map((k) => [k, k])
);

/** Map pre-atlas and legacy 5-bucket labels → atlas specific emotion. */
export const LEGACY_SENTIMENT_TO_EMOTION = {
  // Pre-atlas flat taxonomy
  Joy: "Joyful",
  Sadness: "Sad",
  Anger: "Angry",
  Fear: "Fearful",
  Disgust: "Disgusted",
  Surprise: "Surprised",
  Love: "Loving",
  Guilt: "Guilty",
  Shame: "Ashamed",
  Pride: "Proud",
  Embarrassment: "Embarrassed",
  Jealousy: "Jealous",
  Envy: "Jealous",
  Gratitude: "Thankful",
  Hope: "Hopeful",
  Loneliness: "Lonely",
  Confusion: "Confused",
  Overwhelm: "Overwhelmed",
  Doubt: "Skeptical",
  Clarity: "Focused",
  Frustration: "Frustrated",
  Anxiety: "Anxious",
  Relief: "Relieved",
  Curiosity: "Curious",
  Anticipation: "Excited",
  Rejection: "Hurt",
  Belonging: "Belonging",
  Trust: "Trusting",
  Insecurity: "Insecure",
  Admiration: "Appreciated",
  Resentment: "Hostile",
  Peace: "Peaceful",
  Contentment: "Content",
  Acceptance: "Content",
  Stillness: "Serene",
  Detachment: "Indifferent",
  // Oldest 5-bucket sentiment
  Positive: "Hopeful",
  Neutral: "Content",
  Mixed: "Confused",
  Concerned: "Anxious",
  [CRISIS_EMOTION_KEY]: CRISIS_EMOTION_KEY
};

/** When DB/UI has no usable emotion string — avoid implying calm Content. */
export const DEFAULT_EMOTION_WHEN_MISSING = OTHER_EMOTION_KEY;

function buildLookup() {
  const byKey = new Map();
  for (const category of EMOTION_CATEGORIES) {
    for (const entry of EMOTION_ATLAS[category]) {
      byKey.set(entry.key, {
        key: entry.key,
        label: entry.key,
        category,
        meaning: entry.meaning
      });
    }
  }
  byKey.set(CRISIS_EMOTION_KEY, {
    key: CRISIS_EMOTION_KEY,
    label: CRISIS_EMOTION_KEY,
    category: null,
    meaning: null
  });
  byKey.set(OTHER_EMOTION_KEY, {
    key: OTHER_EMOTION_KEY,
    label: OTHER_EMOTION_KEY,
    category: null,
    meaning: null
  });
  return byKey;
}

const EMOTION_BY_KEY = buildLookup();
const ALLOWED_SET = new Set(REFLECTION_EMOTION_ORDER);

/** Category → Set of specific keys under it. */
const KEYS_BY_CATEGORY = Object.fromEntries(
  EMOTION_CATEGORIES.map((cat) => [cat, new Set(EMOTION_ATLAS[cat].map((e) => e.key))])
);

export const REFLECTION_EMOTION_COLORS = Object.fromEntries([
  ...EMOTION_CATEGORIES.flatMap((cat) =>
    EMOTION_ATLAS[cat].map((e) => [e.key, CATEGORY_COLOR[cat]])
  ),
  [CRISIS_EMOTION_KEY, "#b91c1c"],
  [OTHER_EMOTION_KEY, "#94a3b8"]
]);

/**
 * Meta for a specific emotion (after normalize). Returns null fields for crisis/Other.
 */
export function getEmotionMeta(raw) {
  const key = normalizeReflectionEmotion(raw);
  return EMOTION_BY_KEY.get(key) || EMOTION_BY_KEY.get(OTHER_EMOTION_KEY);
}

export function getCategoryForEmotion(raw) {
  const meta = getEmotionMeta(raw);
  return meta?.category || null;
}

export function isEmotionUnderCategory(specific, category) {
  const set = KEYS_BY_CATEGORY[category];
  if (!set) return false;
  return set.has(normalizeReflectionEmotion(specific));
}

/**
 * Normalize DB/LLM value for aggregation (legacy → atlas; unknown → Other).
 */
export function normalizeReflectionEmotion(raw) {
  const s = (raw || "").trim();
  if (!s) return DEFAULT_EMOTION_WHEN_MISSING;
  if (ALLOWED_SET.has(s)) return s;
  const legacy = LEGACY_SENTIMENT_TO_EMOTION[s];
  if (legacy && ALLOWED_SET.has(legacy)) return legacy;
  return OTHER_EMOTION_KEY;
}

/** Single-token or short phrase outputs the model may use instead of taxonomy labels */
const EMOTION_ALIAS_EXACT = new Map(
  Object.entries({
    anxious: "Anxious",
    anxiety: "Anxious",
    nervous: "Anxious",
    worried: "Anxious",
    worry: "Anxious",
    hurt: "Hurt",
    hurting: "Hurt",
    sad: "Sad",
    sadness: "Sad",
    upset: "Sad",
    depressed: "Depressed",
    angry: "Angry",
    anger: "Angry",
    mad: "Angry",
    furious: "Furious",
    afraid: "Fearful",
    fear: "Fearful",
    scared: "Fearful",
    fearful: "Fearful",
    lonely: "Lonely",
    loneliness: "Lonely",
    confused: "Confused",
    confusion: "Confused",
    unsure: "Skeptical",
    doubt: "Skeptical",
    overwhelmed: "Overwhelmed",
    overwhelm: "Overwhelmed",
    resentful: "Hostile",
    resentment: "Hostile",
    grateful: "Thankful",
    gratitude: "Thankful",
    happy: "Joyful",
    joy: "Joyful",
    joyful: "Joyful",
    excited: "Excited",
    hopeful: "Hopeful",
    hope: "Hopeful",
    jealous: "Jealous",
    jealousy: "Jealous",
    envious: "Jealous",
    envy: "Jealous",
    rejected: "Hurt",
    rejection: "Hurt",
    insecure: "Insecure",
    insecurity: "Insecure",
    frustrated: "Frustrated",
    frustration: "Frustrated",
    relieved: "Relieved",
    relief: "Relieved",
    curious: "Curious",
    curiosity: "Curious",
    ashamed: "Ashamed",
    shame: "Ashamed",
    guilty: "Guilty",
    guilt: "Guilty",
    embarrassed: "Embarrassed",
    embarrassment: "Embarrassed",
    proud: "Proud",
    pride: "Proud",
    disgusted: "Disgusted",
    disgust: "Disgusted",
    surprised: "Surprised",
    surprise: "Surprised",
    peaceful: "Peaceful",
    peace: "Peaceful",
    calm: "Calm",
    content: "Content",
    contentment: "Content",
    accepting: "Content",
    acceptance: "Content",
    detached: "Indifferent",
    detachment: "Indifferent",
    loved: "Loved",
    loving: "Loving",
    love: "Loving",
    belonging: "Belonging",
    trust: "Trusting",
    trusting: "Trusting",
    stressed: "Stressed",
    stress: "Stressed",
    annoyed: "Annoyed",
    irritated: "Irritated",
    rage: "Rage",
    hostile: "Hostile",
    vulnerable: "Vulnerable",
    disappointed: "Disappointed",
    bored: "Bored",
    isolated: "Isolated",
    miserable: "Miserable",
    fragile: "Fragile",
    remorseful: "Remorseful",
    indifferent: "Indifferent",
    skeptical: "Skeptical",
    distrustful: "Distrustful",
    critical: "Critical",
    selfish: "Selfish",
    disapproving: "Disapproving",
    amazed: "Amazed",
    focused: "Focused",
    confident: "Confident",
    courageous: "Courageous",
    empowered: "Empowered",
    safe: "Safe",
    secure: "Secure",
    serene: "Serene",
    relaxed: "Relaxed",
    connected: "Connected",
    sensitive: "Sensitive",
    thoughtful: "Thoughtful",
    valued: "Valued",
    optimistic: "Optimistic",
    playful: "Playful",
    interested: "Interested",
    energetic: "Energetic",
    aroused: "Aroused",
    accepted: "Accepted",
    appreciated: "Appreciated",
    respected: "Respected",
    loyal: "Loyal",
    faithful: "Faithful",
    successful: "Successful",
    creative: "Creative",
    strong: "Strong"
  })
);

const EMOTION_ALIAS_SUBSTRINGS = [
  ["heartbroken", "Hurt"],
  ["heart broken", "Hurt"],
  ["left out", "Insecure"],
  ["not good enough", "Insecure"],
  ["feel invisible", "Lonely"],
  ["stood me up", "Frustrated"],
  ["stood up", "Frustrated"],
  ["self-doubt", "Insecure"],
  ["panic", "Anxious"],
  ["stressed out", "Stressed"]
];

const INFER_TIE_BREAK = [
  "Jealous",
  "Hurt",
  "Insecure",
  "Fearful",
  "Sad",
  "Lonely",
  "Frustrated",
  "Angry",
  "Anxious",
  "Overwhelmed",
  "Skeptical",
  "Confused"
];

/**
 * When the model omits sentiment or returns an unknown label, infer one emotion from the user's own words.
 */
export function inferEmotionFromUserEntry(entry) {
  const t = String(entry || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return null;

  const w = {};
  const add = (k, n) => {
    w[k] = (w[k] || 0) + n;
  };

  if (/\b(jealous|jealousy|envious|envy)\b/.test(t)) add("Jealous", 4);
  if (
    /\b(new (girl|guy|friend|person)|someone new|another (girl|guy|friend))\b/.test(t) &&
    /\b(more time|spend(ing)?|shopping|together|with her|with him|hang out|hanging out)\b/.test(t)
  ) {
    add("Jealous", 4);
  }

  if (/\bthink\b.*\b(don'?t like|doesn'?t like|not like)\b/.test(t)) add("Insecure", 4);
  if (/\b(i feel|feels like|feeling)\b.*\b(reject|abandon|replace|second)\b/.test(t)) add("Insecure", 2);
  if (/\b(left out|insecure|insecurity|compare|comparison|not good enough)\b/.test(t)) add("Insecure", 2);

  if (/\b(doesn'?t like me|don't like me|not like me anymore)\b/.test(t)) add("Hurt", 3);
  if (/\b(reject|rejected|ghosted|abandon)\b/.test(t)) add("Hurt", 2);

  if (/\b(didn'?t show|did not show|never showed|never came|stood me up|no-?show)\b/.test(t)) add("Frustrated", 2);

  if (/\b(afraid|scared|fear|worried).*\b(lose|losing|leave|left|replace)\b/.test(t)) add("Fearful", 3);
  if (/\b(fear of losing|might lose|going to lose)\b/.test(t)) add("Fearful", 2);

  if (/\b(hurt|hurting|heartbroken|heart broken|devastated)\b/.test(t)) add("Hurt", 2);

  if (/\b(lonely|feel alone|so alone)\b/.test(t)) add("Lonely", 2);

  if (/\b(angry|furious|pissed|resent)\b/.test(t)) add("Angry", 2);

  if (/\b(confused|confusion|unclear)\b/.test(t)) add("Confused", 1);

  if (/\b(worried|worry|on edge|nervous)\b/.test(t)) add("Anxious", 1);

  const keys = Object.keys(w);
  if (!keys.length) return null;

  const rank = (e) => {
    const i = INFER_TIE_BREAK.indexOf(e);
    return i === -1 ? 999 : i;
  };
  keys.sort((a, b) => {
    const diff = w[b] - w[a];
    if (diff !== 0) return diff;
    return rank(a) - rank(b);
  });
  return keys[0];
}

/** Map model sentiment string (any casing / alias) to a canonical atlas key. */
export function canonicalEmotionFromModel(raw) {
  const s = (raw || "").trim();
  if (!s) return OTHER_EMOTION_KEY;
  const lower = s.toLowerCase();
  const match = REFLECTION_EMOTION_ORDER.find((e) => e.toLowerCase() === lower);
  if (match) return match;

  const collapsed = lower.replace(/\.+$/, "").trim();
  const aliasHit = EMOTION_ALIAS_EXACT.get(collapsed);
  if (aliasHit) return aliasHit;

  for (const [frag, emo] of EMOTION_ALIAS_SUBSTRINGS) {
    if (lower.includes(frag)) return emo;
  }

  const legacy = LEGACY_SENTIMENT_TO_EMOTION[s] || LEGACY_SENTIMENT_TO_EMOTION[s.replace(/^\w/, (c) => c.toUpperCase())];
  if (legacy && ALLOWED_SET.has(legacy)) return legacy;

  return normalizeReflectionEmotion(s);
}

/**
 * Resolve model output to { sentiment, emotion_category }.
 * Prefer atlas category for the specific emotion; fix mismatched claimed category.
 */
export function resolveEmotionFromModel({ sentiment: rawSentiment, emotion_category: rawCategory } = {}) {
  let sentiment = canonicalEmotionFromModel(rawSentiment);
  if (sentiment === OTHER_EMOTION_KEY && rawSentiment) {
    sentiment = canonicalEmotionFromModel(String(rawSentiment).trim());
  }

  if (sentiment === CRISIS_EMOTION_KEY) {
    return { sentiment, emotion_category: null };
  }

  const atlasCategory = getCategoryForEmotion(sentiment);
  const claimed = (rawCategory || "").trim();
  const claimedNorm = EMOTION_CATEGORIES.find((c) => c.toLowerCase() === claimed.toLowerCase()) || null;

  if (atlasCategory) {
    return { sentiment, emotion_category: atlasCategory };
  }

  if (claimedNorm && sentiment === OTHER_EMOTION_KEY) {
    return { sentiment: OTHER_EMOTION_KEY, emotion_category: null };
  }

  return { sentiment, emotion_category: claimedNorm || null };
}

export function getReflectionEmotionColor(emotionKey) {
  const k = normalizeReflectionEmotion(emotionKey);
  return REFLECTION_EMOTION_COLORS[k] || REFLECTION_EMOTION_COLORS[OTHER_EMOTION_KEY];
}

/** Pie + legend: emotions with count > 0, in taxonomy order */
export function orderedEmotionsWithData(distribution) {
  return REFLECTION_EMOTION_ORDER.filter((key) => (distribution[key] || 0) > 0);
}

/** Prompt fragment listing categories and allowed specifics (exact spelling). */
export function buildEmotionAtlasPromptSection() {
  const lines = EMOTION_CATEGORIES.map((cat) => {
    const keys = EMOTION_ATLAS[cat].map((e) => e.key).join(", ");
    return `- ${cat}: ${keys}`;
  });
  return lines.join("\n");
}
